import { ONFIDO_JS_BUNDLE } from 'apps/auth/constants/Onfido';

type ScriptCallback = () => void;
type LegacyScriptElement = HTMLScriptElement & {
    readyState?: string;
    onreadystatechange?: ((this: HTMLScriptElement, ev: Event) => void) | null;
};

const isBrowserAvailable = (): boolean => typeof document !== 'undefined' && typeof window !== 'undefined';

export const addScript = (filepath?: string, callback?: ScriptCallback): void => {
    if (!filepath || !isBrowserAvailable()) return;

    const scriptElement = document.createElement('script');
    const legacyScriptElement = scriptElement as LegacyScriptElement;
    let done = false;
    const head = document.head || document.getElementsByTagName('head')[0];

    const handleLoad = () => {
        if (done) return;
        done = true;
        callback?.();
        scriptElement.onload = null;
        legacyScriptElement.onreadystatechange = null;
        if (head && scriptElement.parentNode) {
            head.removeChild(scriptElement);
        }
    };

    scriptElement.onload = handleLoad;
    legacyScriptElement.onreadystatechange = () => {
        const readyState = legacyScriptElement.readyState;
        if (!readyState || readyState === 'loaded' || readyState === 'complete') {
            handleLoad();
        }
    };

    scriptElement.setAttribute('type', 'text/javascript');
    scriptElement.setAttribute('src', filepath);

    head?.appendChild(scriptElement);
};

export const addOnfido = (callback?: ScriptCallback): void => {
    addScript(ONFIDO_JS_BUNDLE, callback);
};

export const addStyleLink = (linkPath: string): void => {
    if (!linkPath || !isBrowserAvailable()) return;
    const linkElement = document.createElement('link');
    linkElement.setAttribute('rel', 'stylesheet');
    linkElement.setAttribute('href', linkPath);
    const head = document.head || document.getElementsByTagName('head')[0];
    head?.appendChild(linkElement);
};

export const removeScript = (scriptPath: string): void => {
    if (!isBrowserAvailable()) return;
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i -= 1) {
        const script = scripts[i];
        const src = script?.getAttribute('src');
        if (script && src && src.includes(scriptPath)) {
            script.parentNode?.removeChild(script);
        }
    }
};

export const removeLink = (linkPath: string): void => {
    if (!isBrowserAvailable()) return;
    const links = document.getElementsByTagName('link');
    const regexp = new RegExp(linkPath);
    for (let i = links.length - 1; i >= 0; i -= 1) {
        const link = links[i];
        const href = link?.getAttribute('href');
        if (link && href && regexp.test(href)) {
            link.remove();
        }
    }
};

export const addMetaTag = (name: string, content: string): void => {
    if (!isBrowserAvailable()) return;
    const meta = document.createElement('meta');
    meta.content = content;
    meta.name = name;
    const head = document.head || document.getElementsByTagName('head')[0];
    head?.appendChild(meta);
};
